import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { MessageService } from 'primeng/api';
import { EMPTY, Observable, Subject, Subscription, catchError, forkJoin, map, of, switchMap, throwError } from 'rxjs';

import { AreaAtuacaoService } from '@/core/api/sicom-api/servicos/pessoa-fisica/area-atuacao/area-atuacao.service';
import { AverbacaoNacionalidadeService } from '@/core/api/sicom-api/servicos/pessoa-fisica/averbacao-nacionalidade/averbacao-nacionalidade.service';
import { AverbacaoNomeService } from '@/core/api/sicom-api/servicos/pessoa-fisica/averbacao-nome/averbacao-nome.service';
import { CancelamentoFalecimentoService } from '@/core/api/sicom-api/servicos/pessoa-fisica/cancelamento-falecimento/cancelamento-falecimento.service';
import { CancelamentoInscricaoService } from '@/core/api/sicom-api/servicos/pessoa-fisica/cancelamento-inscricao/cancelamento-inscricao.service';
import { CarteiraProfissionalService } from '@/core/api/sicom-api/servicos/pessoa-fisica/carteira-profissional/carteira-profissional.service';
import { CedulaIdentidadeService } from '@/core/api/sicom-api/servicos/pessoa-fisica/cedula-identidade/cedula-identidade.service';
import { ConsultorioPrivadoService } from '@/core/api/sicom-api/servicos/pessoa-fisica/consultorio-privado/consultorio-privado.service';
import { DiretorClinicoService } from '@/core/api/sicom-api/servicos/pessoa-fisica/diretor-clinico/diretor-clinico.service';
import { EspecialidadeMedicaService } from '@/core/api/sicom-api/servicos/pessoa-fisica/especialidade-medica/especialidade-medica.service';
import { MedicoPCSMOService } from '@/core/api/sicom-api/servicos/pessoa-fisica/pcmso/pcmso.service';
import { PosGraduacaoService } from '@/core/api/sicom-api/servicos/pessoa-fisica/pos-graduacao/pos-graduacao.service';
import { PreMedicoService } from '@/core/api/sicom-api/servicos/pessoa-fisica/pre-medico/pre-medico.service';
import { VistoCorridoService } from '@/core/api/sicom-api/servicos/pessoa-fisica/visto-corrido/visto-corrido.service';
import { AlteracaoCadastralService as AlteracaoCadastralPessoaJuridicaService } from '@/core/api/sicom-api/servicos/pessoa-juridica/alteracao-cadastral/alteracao-cadastral.service';
import { CancelamentoPjService } from '@/core/api/sicom-api/servicos/pessoa-juridica/cancelamento-pj/cancelamento-pj.service';
import { CartEventoService as CartEventoPessoaJuridicaService } from '@/core/api/sicom-api/servicos/pessoa-juridica/cart-evento/cart-evento.service';
import { ComissaoEticaService } from '@/core/api/sicom-api/servicos/pessoa-juridica/comissao-etica/comissao-etica.service';
import { PrePrestadorService } from '@/core/api/sicom-api/servicos/pessoa-juridica/pre-prestador/pre-prestador.service';
import { RegimentoInternoService } from '@/core/api/sicom-api/servicos/pessoa-juridica/regimento-interno/regimento-interno.service';
import { RenovacaoCadastralService } from '@/core/api/sicom-api/servicos/pessoa-juridica/renovacao-cadastral/renovacao-cadastral.service';
import { SolicitacaoDiretorService } from '@/core/api/sicom-api/servicos/pessoa-juridica/solicitacao-diretor/solicitacao-diretor.service';
import { SolicitacaoDiretorTecnicoService } from '@/core/api/sicom-api/servicos/pessoa-juridica/solicitacao-diretor-tecnico/solicitacao-diretor-tecnico.service';
import { ProtocoloTramitacaoService } from '@/core/api/sicom-api/servicos/shared/protocolo-tramitacao.service';
import { type ServicoGeralResponse } from '@/core/api/sicom-api/servicos/shared/servico-geral.model';
import { ServicoGeralService } from '@/core/api/sicom-api/servicos/shared/servico-geral.service';
import { KanbanServicosService } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.service';
import { LoggerService } from '@/core/services/logger.service';
import { ServicoPendenciaService } from '@/core/api/sicom-api/servicos/shared/servico-pendencia.service';
import { obterServicoDisponivelPorCodigo } from '@/features/servicos/shared/enums/servico-disponivel.enum';
import { type InserirHistoricoPayload, type ProtocoloResumo, type TramitacaoHistoricoCard } from '@/features/servicos/shared/card-servicos/card-servicos';
import { type ConsultaProtocoloRequest, type ServicosAbertosPorChave, consultarAbertosPorCodigoServico, consultarProtocoloPorCodigoServico } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos-abertos.registry';
import { mapearHistoricoTramitacaoCard, mapearItemStatusProtocolo } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos.mapper';
import { type ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import { type AcaoServico, campoDaConfirmacao, chaveDaDataDeConfirmacao } from '@/features/servicos/shared/utils/servicos-acoes.utils';
import { CustomService } from '@/shared/services/custom.service';

interface ConsultarHistoricoRequest {
    numeroProtocolo: string;
    anoProtocolo: string;
    uf: string;
    protocoloSelecionado: ProtocoloResumo | null;
}

interface InserirHistoricoRequest {
    numeroProtocolo: string;
    anoProtocolo: string;
    ufProtocolo: string;
    payload: InserirHistoricoPayload;
    protocoloSelecionado: ProtocoloResumo | null;
}

interface ConfirmarAverbacaoNomeRequest {
    numeroProtocolo: string;
    anoProtocolo: string;
    ufCrm: string;
    codigoServico: string;
    dtCancelamento?: string;
    dtHomologacao?: string;
    dataDiretoria?: string;
    observacao?: string;
    /**
     * A efetivação pedida é a inscrição de OFÍCIO, e não a comum.
     *
     * Viaja no request porque é o único jeito de os dois gatilhos — o botão do detalhe e o arraste
     * para Concluídos — chegarem ao mesmo desvio dizendo a mesma coisa. Quem decide é a tela, com
     * `efetivacaoSeraExOfficio`, e é ela quem nomeou o ato ao analista: recalcular aqui admitiria
     * que a confirmação descrevesse um ato e a chamada praticasse outro.
     *
     * Ausente ou `false` é o caminho de sempre, então nada muda para os demais serviços.
     */
    exOfficio?: boolean;
}

/** O que a conclusão de uma alteração precisa dizer à tela. */
export interface AlteracaoSolicitacaoConcluida {
    /** Segura o detalhe aberto quando há algo para o analista ler — hoje, o CRM gerado. */
    manterDetalheAberto: boolean;
}

/** Contrato mínimo dos serviços que suportam consultar+cancelar uma solicitação (DTOs heterogêneos → `unknown`). */
interface CancelavelSolicitacaoService {
    consultar(params: { numeroProtocolo: string; anoProtocolo: string; ufCrm: string }): Observable<unknown>;
    cancelarSolicitacao(dto: unknown, motivo?: string): Observable<unknown>;
}

/** Contrato mínimo dos serviços que suportam consultar+efetivar (DTOs heterogêneos → `unknown`). */
interface EfetivavelSolicitacaoService {
    consultar(params: { numeroProtocolo: string; anoProtocolo: string; ufCrm: string }): Observable<unknown>;
    efetivarSolicitacao(dto: unknown): Observable<unknown>;
    /**
     * Inscrição de ofício, por decurso de prazo — o único caminho que efetiva sem pagamento.
     *
     * OPCIONAL porque só o pré-prestador a tem: é lá que o Conselho inscreve a empresa inerte. Torná-la
     * obrigatória forçaria as outras duas dezenas de serviços a declarar um método que não existe no
     * backend deles, e o corpo mais provável seria delegar ao `efetivarSolicitacao` — que é exatamente
     * o fallback silencioso que este desenho existe para impedir.
     */
    efetivarSolicitacaoExOfficio?(dto: unknown): Observable<unknown>;
}

/** Lê o CRM da resposta sem assumir formato: serviço que não gera registro devolve corpo vazio. */
function extrairCrmGerado(resposta: unknown): number | null {
    if (resposta && typeof resposta === 'object' && 'crmGerado' in resposta) {
        const valor = (resposta as { crmGerado: unknown }).crmGerado;
        return typeof valor === 'number' ? valor : null;
    }
    return null;
}

@Injectable()
export class DashboardServicosFacade {
    private servicosGeraisSub?: Subscription;
    private protocolosSub?: Subscription;
    private consultaProtocoloSub?: Subscription;
    private historicoTramitacaoSub?: Subscription;
    private inserirHistoricoSub?: Subscription;
    private confirmarSolicitacaoSub?: Subscription;
    private anotacaoQuadroSub?: Subscription;

    readonly servicosGerais = signal<ServicoGeralResponse[]>([]);
    readonly itensProtocolos = signal<ItemStatus[]>([]);
    readonly carregandoProtocolos = signal(false);
    readonly carregandoConsultaProtocolo = signal(false);
    readonly erroConsultaProtocolo = signal<string | null>(null);
    readonly historicoTramitacoes = signal<TramitacaoHistoricoCard[]>([]);
    readonly carregandoHistorico = signal(false);
    readonly erroHistorico = signal<string | null>(null);
    readonly inserindoHistorico = signal(false);
    readonly erroInsercaoHistorico = signal<string | null>(null);
    readonly confirmandoSolicitacao = signal(false);
    /**
     * Recusa da última finalização (efetivar ou cancelar).
     *
     * É o retorno que a tela de detalhe exibe, e não um estado interno: o toast do erro de API vem do
     * interceptor central, que se cala quando o corpo do erro não traz texto — sem alguém renderizando
     * este sinal, uma recusa sem corpo (o 403 de quem não tem a role, por exemplo) vira clique sem
     * reação nenhuma. Quem o lê é o `CardServicos`, via `DashboardServicos`.
     */
    readonly erroConfirmacaoSolicitacao = signal<string | null>(null);
    readonly sucessoConfirmacaoSolicitacao = signal<string | null>(null);
    /** CRM atribuído pela efetivação que acabou de ocorrer. Nulo nos serviços que não geram registro. */
    readonly crmGeradoConfirmacao = signal<number | null>(null);
    private readonly alteracaoSolicitacaoConcluidaSubject = new Subject<AlteracaoSolicitacaoConcluida>();
    readonly alteracaoSolicitacaoConcluida$ = this.alteracaoSolicitacaoConcluidaSubject.asObservable();

    private readonly servicosAbertosPorChave: ServicosAbertosPorChave;

    constructor(
        private readonly servicoGeralService: ServicoGeralService,
        private readonly areaAtuacaoService: AreaAtuacaoService,
        private readonly averbacaoNacionalidadeService: AverbacaoNacionalidadeService,
        private readonly averbacaoNomeService: AverbacaoNomeService,
        private readonly cancelamentoFalecimentoService: CancelamentoFalecimentoService,
        private readonly carteiraProfissionalService: CarteiraProfissionalService,
        private readonly cedulaIdentidadeService: CedulaIdentidadeService,
        private readonly especialidadeMedicaService: EspecialidadeMedicaService,
        private readonly vistoCorridoService: VistoCorridoService,
        private readonly preMedicoService: PreMedicoService,
        private readonly cancelamentoInscricaoService: CancelamentoInscricaoService,
        private readonly consultorioPrivadoService: ConsultorioPrivadoService,
        private readonly medicoPCSMOService: MedicoPCSMOService,
        private readonly posGraduacaoService: PosGraduacaoService,
        private readonly regimentoInternoService: RegimentoInternoService,
        private readonly comissaoEticaService: ComissaoEticaService,
        private readonly alteracaoCadastralPessoaJuridicaService: AlteracaoCadastralPessoaJuridicaService,
        private readonly cancelamentoPjService: CancelamentoPjService,
        private readonly diretorClinicoService: DiretorClinicoService,
        private readonly solicitacaoDiretorService: SolicitacaoDiretorService,
        private readonly prePrestadorService: PrePrestadorService,
        private readonly renovacaoCadastralService: RenovacaoCadastralService,
        private readonly solicitacaoDiretorTecnicoService: SolicitacaoDiretorTecnicoService,
        private readonly cartEventoPessoaJuridicaService: CartEventoPessoaJuridicaService,
        private readonly protocoloTramitacaoService: ProtocoloTramitacaoService,
        private readonly servicoPendenciaService: ServicoPendenciaService,
        private readonly kanbanServicosService: KanbanServicosService,
        private readonly messageService: MessageService,
        private readonly customService: CustomService,
        private readonly logger: LoggerService
    ) {
        this.servicosAbertosPorChave = {
            areaAtuacao: this.areaAtuacaoService,
            averbacaoNacionalidade: this.averbacaoNacionalidadeService,
            averbacaoNome: this.averbacaoNomeService,
            cancelamentoFalecimento: this.cancelamentoFalecimentoService,
            carteiraProfissional: this.carteiraProfissionalService,
            cedulaIdentidade: this.cedulaIdentidadeService,
            especialidadeMedica: this.especialidadeMedicaService,
            vistoCorrido: this.vistoCorridoService,
            preMedico: this.preMedicoService,
            cancelamentoInscricao: this.cancelamentoInscricaoService,
            consultorioPrivado: this.consultorioPrivadoService,
            medicoPcmso: this.medicoPCSMOService,
            posGraduacao: this.posGraduacaoService,
            regimentoInterno: this.regimentoInternoService,
            comissaoEtica: this.comissaoEticaService,
            alteracaoCadastralPessoaJuridica: this.alteracaoCadastralPessoaJuridicaService,
            cancelamentoPj: this.cancelamentoPjService,
            diretorClinico: this.diretorClinicoService,
            solicitacaoDiretor: this.solicitacaoDiretorService,
            prePrestador: this.prePrestadorService,
            renovacaoCadastral: this.renovacaoCadastralService,
            solicitacaoDiretorTecnico: this.solicitacaoDiretorTecnicoService,
            cartEvento: this.cartEventoPessoaJuridicaService
        };
    }

    carregarServicosGerais(ufCrm: string): void {
        this.servicosGeraisSub?.unsubscribe();
        this.servicosGeraisSub = this.servicoGeralService.consultar({ ufCrm }).subscribe({
            next: (response) => {
                this.servicosGerais.set(response ?? []);
            },
            error: () => {
                this.servicosGerais.set([]);
            }
        });
    }

    limparProtocolos(): void {
        this.protocolosSub?.unsubscribe();
        this.itensProtocolos.set([]);
        this.carregandoProtocolos.set(false);
    }

    limparConsultaProtocolo(): void {
        this.consultaProtocoloSub?.unsubscribe();
        this.carregandoConsultaProtocolo.set(false);
        this.erroConsultaProtocolo.set(null);
    }

    carregarProtocolosPorServico(codigoServico: string, ufsCrm: string[]): void {
        this.carregandoProtocolos.set(true);
        this.protocolosSub?.unsubscribe();

        const ufsConsulta = this.normalizarUfsConsulta(ufsCrm);
        if (!ufsConsulta.length) {
            this.itensProtocolos.set([]);
            this.carregandoProtocolos.set(false);
            return;
        }

        try {
            const chamadasAbertos = ufsConsulta.map((ufCrm) => consultarAbertosPorCodigoServico(codigoServico, ufCrm, this.servicosAbertosPorChave).pipe(catchError(() => of([] as Record<string, unknown>[]))));

            this.protocolosSub = forkJoin(chamadasAbertos)
                .pipe(
                    switchMap((respostasPorUf) => {
                        const itensAgrupados = respostasPorUf.flatMap((response, indiceUf) => (response ?? []).map((item, indiceItem) => mapearItemStatusProtocolo(item, indiceItem, ufsConsulta[indiceUf])));
                        return this.enriquecerModalidadePrePrestador(codigoServico, this.deduplicarItensProtocolos(itensAgrupados));
                    })
                )
                .subscribe({
                    next: (itens) => {
                        this.itensProtocolos.set(itens);
                        this.carregandoProtocolos.set(false);
                    },
                    error: () => {
                        this.itensProtocolos.set([]);
                        this.carregandoProtocolos.set(false);
                    }
                });
        } catch {
            this.itensProtocolos.set([]);
            this.carregandoProtocolos.set(false);
        }
    }

    consultarProtocoloPorServico(codigoServico: string, request: ConsultaProtocoloRequest, onSuccess: (item: ItemStatus) => void): void {
        this.consultaProtocoloSub?.unsubscribe();
        this.carregandoConsultaProtocolo.set(true);
        this.erroConsultaProtocolo.set(null);

        try {
            this.consultaProtocoloSub = consultarProtocoloPorCodigoServico(codigoServico, request, this.servicosAbertosPorChave).subscribe({
                next: (response) => {
                    const item = mapearItemStatusProtocolo(this.normalizarResponseConsulta(response), 0, request.ufCrm, {
                        numeroProtocolo: request.numeroProtocolo,
                        anoProtocolo: request.anoProtocolo,
                        ufCrm: request.ufCrm
                    });

                    this.carregandoConsultaProtocolo.set(false);
                    this.erroConsultaProtocolo.set(null);
                    onSuccess(item);
                },
                error: (error: unknown) => {
                    this.carregandoConsultaProtocolo.set(false);
                    this.erroConsultaProtocolo.set(this.customService.extractBackendErrorMessage(error, 'Falha ao consultar protocolo do serviço selecionado.'));
                }
            });
        } catch (error) {
            this.carregandoConsultaProtocolo.set(false);
            this.erroConsultaProtocolo.set(this.customService.extractBackendErrorMessage(error, 'Serviço sem endpoint de consulta mapeado.'));
        }
    }

    consultarDadosProtocoloPorServico(codigoServico: string, request: ConsultaProtocoloRequest): Observable<Record<string, unknown>> {
        return consultarProtocoloPorCodigoServico(codigoServico, request, this.servicosAbertosPorChave).pipe(map((response) => this.normalizarResponseConsulta(response)));
    }

    carregarHistoricoTramitacao(request: ConsultarHistoricoRequest): void {
        this.carregandoHistorico.set(true);
        this.erroHistorico.set(null);
        this.historicoTramitacaoSub?.unsubscribe();
        this.historicoTramitacaoSub = this.protocoloTramitacaoService
            .consultar({
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                uf: request.uf
            })
            .subscribe({
                next: (response) => {
                    this.historicoTramitacoes.set((response ?? []).map((item) => mapearHistoricoTramitacaoCard(item, request.protocoloSelecionado)));
                    this.carregandoHistorico.set(false);
                },
                error: () => {
                    this.historicoTramitacoes.set([]);
                    this.erroHistorico.set('Falha ao carregar histórico de tramitações.');
                    this.carregandoHistorico.set(false);
                }
            });
    }

    inserirHistoricoTramitacao(request: InserirHistoricoRequest): void {
        this.inserindoHistorico.set(true);
        this.erroInsercaoHistorico.set(null);
        this.inserirHistoricoSub?.unsubscribe();
        this.inserirHistoricoSub = this.protocoloTramitacaoService
            .atualizar({
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                textoInformativo: request.payload.textoInformativo,
                ufProtocolo: request.ufProtocolo
            })
            .subscribe({
                next: () => {
                    this.inserindoHistorico.set(false);
                    this.erroInsercaoHistorico.set(null);
                    this.carregarHistoricoTramitacao({
                        numeroProtocolo: request.numeroProtocolo,
                        anoProtocolo: request.anoProtocolo,
                        uf: request.ufProtocolo,
                        protocoloSelecionado: request.protocoloSelecionado
                    });
                },
                error: () => {
                    this.inserindoHistorico.set(false);
                    this.erroInsercaoHistorico.set('Falha ao inserir novo histórico de tramitação.');
                }
            });
    }

    /**
     * Finaliza a solicitação no portal e devolve o resultado ao chamador, em vez de tratá-lo aqui
     * dentro. É o que permite ao quadro só gravar o movimento depois do OK do portal — os métodos
     * `void` abaixo continuam existindo para a tela de detalhe, e passam a ser casca disto.
     */
    finalizarSolicitacao(request: ConfirmarAverbacaoNomeRequest, acao: AcaoServico, motivo?: string): Observable<void> {
        return this.finalizarSolicitacaoComResposta(request, acao, motivo).pipe(map(() => void 0));
    }

    /**
     * Mesma cadeia de `finalizarSolicitacao`, mas sem descartar a resposta do portal — é o que
     * permite a `executarFinalizacao` capturar o CRM gerado na efetivação sem duplicar a montagem do
     * DTO nem o despacho consultar → efetivar/cancelar.
     */
    /**
     * Existe endpoint para esta ação neste serviço? Pergunta que só o encerramento em lote faz, e
     * ANTES de disparar: sem endpoint, `finalizarSolicitacao` erra na hora da chamada — no fluxo
     * unitário isso é uma falha isolada, mas num lote de trinta seriam trinta falhas idênticas, todas
     * evitáveis. `rolesDoServico` não responde por isto: código fora de `ROLES_POR_CODIGO` mantém o
     * default ABERTO, então o gate de permissão deixa passar exatamente o serviço que aqui falha.
     *
     * Resolve pelo mesmo `obterServicoDaAcao` que a chamada de verdade usa — duplicar a lista aqui
     * seria criar duas verdades sobre o mesmo mapa, e a divergência apareceria só em produção.
     */
    temEndpointDaAcao(acao: AcaoServico, codigoServico: string): boolean {
        return this.obterServicoDaAcao(acao, codigoServico) !== null;
    }

    private obterServicoDaAcao(acao: AcaoServico, codigoServico: string): EfetivavelSolicitacaoService | CancelavelSolicitacaoService | null {
        return acao === 'efetivar' ? this.obterServicoEfetivacao(codigoServico) : (this.obterServicoCancelamentoPessoaFisica(codigoServico) ?? this.obterServicoCancelamentoPessoaJuridica(codigoServico));
    }

    private finalizarSolicitacaoComResposta(request: ConfirmarAverbacaoNomeRequest, acao: AcaoServico, motivo?: string): Observable<unknown> {
        const servico = this.obterServicoDaAcao(acao, request.codigoServico);

        if (!servico) {
            return throwError(() => new Error(`Serviço sem endpoint de ${acao} mapeado: ${request.codigoServico}`));
        }

        return servico
            .consultar({
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                ufCrm: request.ufCrm
            })
            .pipe(
                map((dto) => this.completarDtoQuandoOServicoExige(dto, request, acao)),
                switchMap((dto) => (acao === 'efetivar' ? this.despacharEfetivacao(servico as EfetivavelSolicitacaoService, dto, request) : (servico as CancelavelSolicitacaoService).cancelarSolicitacao(dto, motivo)))
            );
    }

    /**
     * O ÚNICO ponto em que a efetivação comum e a de ofício se separam.
     *
     * Único de propósito: os dois gatilhos (botão do detalhe e arraste para Concluídos) convergem em
     * `finalizarSolicitacaoComResposta`, e é aqui que a bifurcação cabe inteira. Uma segunda
     * implementação em qualquer dos dois caminhos é como eles voltam a divergir — um mandando ao
     * portal o ato que a tela nomeou, o outro mandando o de sempre.
     *
     * Serviço que recebeu o pedido de ofício e não tem o endpoint ERRA, e o erro sobe pela mesma
     * cadeia que qualquer recusa: cair no `efetivarSolicitacao` comum seria praticar no portal um ato
     * DIFERENTE do que a confirmação descreveu — inscrição paga onde o analista autorizou inscrição
     * sem pagamento —, e ninguém veria a troca, porque a chamada teria sucesso.
     */
    private despacharEfetivacao(servico: EfetivavelSolicitacaoService, dto: unknown, request: ConfirmarAverbacaoNomeRequest): Observable<unknown> {
        if (!request.exOfficio) {
            return servico.efetivarSolicitacao(dto);
        }

        if (!servico.efetivarSolicitacaoExOfficio) {
            return throwError(() => new Error(`Serviço sem endpoint de inscrição ex officio mapeado: ${request.codigoServico}`));
        }

        return servico.efetivarSolicitacaoExOfficio(dto);
    }

    /**
     * O que a tela escreve quando a finalização é recusada.
     *
     * `extractBackendErrorMessage` prefere o `error.message` ao fallback, e num `HttpErrorResponse`
     * esse campo é jargão de transporte — "Http failure response for …: 403 Forbidden". Isso passava
     * enquanto ninguém lia o sinal; agora que o card o exibe, mostrar a linha de transporte ao analista
     * seria pior que a frase do ato, que ao menos diz o que não aconteceu. Então: vale o que o servidor
     * mandou no corpo; na ausência dele, a frase do ato.
     *
     * A comparação com `error.message` é como se distingue "o corpo falou" de "não havia corpo" sem
     * reimplementar aqui a leitura do `CfmErrorDto`, que mora no `CustomService`.
     */
    private mensagemDaRecusa(error: unknown, fallback: string): string {
        const mensagem = this.customService.extractBackendErrorMessage(error, fallback);
        return error instanceof HttpErrorResponse && mensagem === (error.message ?? '').trim() ? fallback : mensagem;
    }

    /** Casca comum dos métodos `void`: sinais de estado, toast e recarga da lista. */
    private executarFinalizacao(request: ConfirmarAverbacaoNomeRequest, acao: AcaoServico, motivo: string | undefined, mensagemSucesso: string, mensagemErro: string): void {
        this.confirmandoSolicitacao.set(true);
        this.erroConfirmacaoSolicitacao.set(null);
        this.sucessoConfirmacaoSolicitacao.set(null);
        this.crmGeradoConfirmacao.set(null);
        this.confirmarSolicitacaoSub?.unsubscribe();

        this.confirmarSolicitacaoSub = this.finalizarSolicitacaoComResposta(request, acao, motivo).subscribe({
            next: (resposta: unknown) => {
                this.confirmandoSolicitacao.set(false);
                this.erroConfirmacaoSolicitacao.set(null);
                this.finalizarAlteracaoSolicitacao(request, mensagemSucesso, extrairCrmGerado(resposta));
            },
            error: (error: unknown) => {
                this.confirmandoSolicitacao.set(false);
                this.sucessoConfirmacaoSolicitacao.set(null);
                this.crmGeradoConfirmacao.set(null);
                this.erroConfirmacaoSolicitacao.set(this.mensagemDaRecusa(error, mensagemErro));
            }
        });
    }

    confirmarSolicitacaoAverbacaoNome(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de averbação de nome.');
    }

    confirmarSolicitacaoAverbacaoNacionalidade(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de averbação de nacionalidade.');
    }

    confirmarSolicitacaoCancelamentoFalecimento(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de cancelamento por falecimento.');
    }

    confirmarSolicitacaoCancelamentoInscricao(request: ConfirmarAverbacaoNomeRequest): void {
        this.confirmandoSolicitacao.set(true);
        this.erroConfirmacaoSolicitacao.set(null);
        this.sucessoConfirmacaoSolicitacao.set(null);
        this.confirmarSolicitacaoSub?.unsubscribe();

        this.confirmarSolicitacaoSub = this.cancelamentoInscricaoService
            .consultar({
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                ufCrm: request.ufCrm
            })
            .pipe(
                switchMap((dto) =>
                    this.cancelamentoInscricaoService.efetivarSolicitacao({
                        ...dto,
                        // Data do cancelamento informada pelo analista; na ausencia, usa a data
                        // atual (ato administrativo), nao a data da solicitacao.
                        dtCancelamento: request.dtCancelamento ?? this.dataAtualFormatada()
                    })
                )
            )
            .subscribe({
                next: () => {
                    this.confirmandoSolicitacao.set(false);
                    this.erroConfirmacaoSolicitacao.set(null);
                    this.finalizarAlteracaoSolicitacao(request, 'Solicitação confirmada com sucesso.');
                },
                error: (error: unknown) => {
                    this.confirmandoSolicitacao.set(false);
                    this.sucessoConfirmacaoSolicitacao.set(null);
                    this.erroConfirmacaoSolicitacao.set(this.mensagemDaRecusa(error, 'Falha ao confirmar solicitação de cancelamento de inscrição.'));
                }
            });
    }

    confirmarSolicitacaoCancelamentoPj(request: ConfirmarAverbacaoNomeRequest): void {
        this.confirmandoSolicitacao.set(true);
        this.erroConfirmacaoSolicitacao.set(null);
        this.sucessoConfirmacaoSolicitacao.set(null);
        this.confirmarSolicitacaoSub?.unsubscribe();

        this.confirmarSolicitacaoSub = this.cancelamentoPjService
            .consultar({
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                ufCrm: request.ufCrm
            })
            .pipe(
                switchMap((dto) =>
                    this.cancelamentoPjService.efetivarSolicitacao({
                        ...dto,
                        dtCancelamento: request.dtCancelamento ?? dto?.dtCancelamento ?? this.extrairDataCancelamento(dto?.dataHora)
                    })
                )
            )
            .subscribe({
                next: () => {
                    this.confirmandoSolicitacao.set(false);
                    this.erroConfirmacaoSolicitacao.set(null);
                    this.finalizarAlteracaoSolicitacao(request, 'Solicitação confirmada com sucesso.');
                },
                error: (error: unknown) => {
                    this.confirmandoSolicitacao.set(false);
                    this.sucessoConfirmacaoSolicitacao.set(null);
                    this.erroConfirmacaoSolicitacao.set(this.mensagemDaRecusa(error, 'Falha ao confirmar solicitação de cancelamento de pessoa jurídica.'));
                }
            });
    }

    confirmarSolicitacaoAlteracaoCadastralPj(request: ConfirmarAverbacaoNomeRequest): void {
        this.confirmandoSolicitacao.set(true);
        this.erroConfirmacaoSolicitacao.set(null);
        this.sucessoConfirmacaoSolicitacao.set(null);
        this.confirmarSolicitacaoSub?.unsubscribe();

        this.confirmarSolicitacaoSub = this.alteracaoCadastralPessoaJuridicaService
            .consultar({
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                ufCrm: request.ufCrm
            })
            .pipe(
                switchMap((dto) =>
                    this.alteracaoCadastralPessoaJuridicaService.efetivarSolicitacao({
                        ...dto,
                        codigoServico: request.codigoServico,
                        observacao: request.observacao
                    })
                )
            )
            .subscribe({
                next: () => {
                    this.confirmandoSolicitacao.set(false);
                    this.erroConfirmacaoSolicitacao.set(null);
                    this.finalizarAlteracaoSolicitacao(request, 'Solicitação confirmada com sucesso.');
                },
                error: (error: unknown) => {
                    this.confirmandoSolicitacao.set(false);
                    this.sucessoConfirmacaoSolicitacao.set(null);
                    this.erroConfirmacaoSolicitacao.set(this.mensagemDaRecusa(error, 'Falha ao confirmar solicitação de alteração cadastral.'));
                }
            });
    }

    confirmarSolicitacaoConsultorioPrivado(request: ConfirmarAverbacaoNomeRequest): void {
        this.confirmandoSolicitacao.set(true);
        this.erroConfirmacaoSolicitacao.set(null);
        this.sucessoConfirmacaoSolicitacao.set(null);
        this.confirmarSolicitacaoSub?.unsubscribe();

        this.confirmarSolicitacaoSub = this.consultorioPrivadoService
            .consultar({
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                ufCrm: request.ufCrm
            })
            .pipe(
                map((dto) => ({ ...dto, codigoServico: request.codigoServico })),
                switchMap((dto) => this.consultorioPrivadoService.efetivarSolicitacao(dto))
            )
            .subscribe({
                next: () => {
                    this.confirmandoSolicitacao.set(false);
                    this.erroConfirmacaoSolicitacao.set(null);
                    this.finalizarAlteracaoSolicitacao(request, 'Solicitação confirmada com sucesso.');
                },
                error: (error: unknown) => {
                    this.confirmandoSolicitacao.set(false);
                    this.sucessoConfirmacaoSolicitacao.set(null);
                    this.erroConfirmacaoSolicitacao.set(this.mensagemDaRecusa(error, 'Falha ao confirmar solicitação de consultório privado.'));
                }
            });
    }

    confirmarSolicitacaoPcmso(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de PCMSO.');
    }

    confirmarSolicitacaoVistoCorrido(request: ConfirmarAverbacaoNomeRequest): void {
        this.confirmandoSolicitacao.set(true);
        this.erroConfirmacaoSolicitacao.set(null);
        this.sucessoConfirmacaoSolicitacao.set(null);
        this.confirmarSolicitacaoSub?.unsubscribe();

        this.confirmarSolicitacaoSub = this.vistoCorridoService
            .consultar({
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                ufCrm: request.ufCrm,
                codigoServico: request.codigoServico
            })
            .pipe(
                map((dto) => ({ ...(dto ?? {}), codigoServico: request.codigoServico })),
                switchMap((dto) => this.vistoCorridoService.efetivarSolicitacao(dto))
            )
            .subscribe({
                next: () => {
                    this.confirmandoSolicitacao.set(false);
                    this.erroConfirmacaoSolicitacao.set(null);
                    this.finalizarAlteracaoSolicitacao(request, 'Solicitação confirmada com sucesso.');
                },
                error: (error: unknown) => {
                    this.confirmandoSolicitacao.set(false);
                    this.sucessoConfirmacaoSolicitacao.set(null);
                    this.erroConfirmacaoSolicitacao.set(this.mensagemDaRecusa(error, 'Falha ao confirmar solicitação de visto corrido.'));
                }
            });
    }
    confirmarSolicitacaoPosGraduacao(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de pos-graduação.');
    }

    confirmarSolicitacaoPreMedico(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de pre-médico.');
    }

    confirmarSolicitacaoPrePrestador(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de pre-prestador.');
    }

    confirmarSolicitacaoRegimentoInterno(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de regimento interno.');
    }

    confirmarSolicitacaoComissaoEtica(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de comissão de ética.');
    }

    confirmarSolicitacaoCartEvento(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de cart evento.');
    }

    confirmarSolicitacaoSolicitacaoDiretor(request: ConfirmarAverbacaoNomeRequest): void {
        this.confirmandoSolicitacao.set(true);
        this.erroConfirmacaoSolicitacao.set(null);
        this.sucessoConfirmacaoSolicitacao.set(null);
        this.confirmarSolicitacaoSub?.unsubscribe();

        this.confirmarSolicitacaoSub = this.solicitacaoDiretorService
            .consultar({
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                ufCrm: request.ufCrm
            })
            .pipe(
                switchMap((dto) => {
                    if (dto.requerConfirmacaoDiretorSocio && !dto.aprovado) {
                        const confirmou = window.confirm('O diretor técnico atual é sócio. Confirma a substituição?');
                        if (!confirmou) {
                            this.confirmandoSolicitacao.set(false);
                            return EMPTY;
                        }
                        return this.solicitacaoDiretorService.efetivarSolicitacao({ ...dto, aprovado: 'S', dataDiretoria: request.dataDiretoria });
                    }
                    return this.solicitacaoDiretorService.efetivarSolicitacao({ ...dto, dataDiretoria: request.dataDiretoria });
                })
            )
            .subscribe({
                next: () => {
                    this.confirmandoSolicitacao.set(false);
                    this.erroConfirmacaoSolicitacao.set(null);
                    this.finalizarAlteracaoSolicitacao(request, 'Solicitação confirmada com sucesso.');
                },
                error: (error: unknown) => {
                    this.confirmandoSolicitacao.set(false);
                    this.sucessoConfirmacaoSolicitacao.set(null);
                    this.erroConfirmacaoSolicitacao.set(this.mensagemDaRecusa(error, 'Falha ao confirmar solicitação de diretor técnico.'));
                }
            });
    }

    confirmarSolicitacaoSolicitacaoDiretorTecnico(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de diretor técnico.');
    }

    confirmarSolicitacaoDiretorClinico(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de diretor clínico.');
    }

    confirmarSolicitacaoRenovacaoPj(request: ConfirmarAverbacaoNomeRequest): void {
        this.executarFinalizacao(request, 'efetivar', undefined, 'Solicitação confirmada com sucesso.', 'Falha ao confirmar solicitação de renovação PJ.');
    }

    /**
     * Acrescenta uma linha na tramitação local (quadro) do protocolo.
     *
     * Silencioso no erro de propósito: é registro complementar de um aviso que JÁ saiu por e-mail.
     * Um toast vermelho aqui faria o analista achar que o médico não foi avisado, e o e-mail não
     * volta atrás. A falha vai para o log.
     */
    registrarLinhaNaTramitacaoLocal(numeroProtocolo: string, anoProtocolo: string, texto: string, emailHtml?: string): void {
        const numero = Number(numeroProtocolo);
        const ano = Number(anoProtocolo);
        if (!Number.isFinite(numero) || !Number.isFinite(ano) || !texto.trim()) {
            return;
        }

        this.anotacaoQuadroSub?.unsubscribe();
        this.anotacaoQuadroSub = this.kanbanServicosService.anotar({ ano, numero, texto: texto.trim(), emailHtml }).subscribe({
            error: (erro: unknown) => this.logger.error('Falha ao registrar a linha na tramitação local do quadro:', erro)
        });
    }

    avisarPendenciaServico(request: ConfirmarAverbacaoNomeRequest, observacao?: string): void {
        const servicoConfig = obterServicoDisponivelPorCodigo(request.codigoServico);
        if (!servicoConfig) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: `Serviço não identificado para avisar pendência: ${request.codigoServico}`
            });
            return;
        }

        this.confirmarSolicitacaoSub?.unsubscribe();
        this.confirmarSolicitacaoSub = this.servicoPendenciaService
            .avisarPendencia(servicoConfig.id, request.ufCrm, {
                numeroProtocolo: request.numeroProtocolo,
                anoProtocolo: request.anoProtocolo,
                observacao
            })
            .subscribe({
                next: () => {
                    this.finalizarAlteracaoSolicitacao(request, 'Solicitação marcada como pendente.');
                },
                error: (error: unknown) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: this.customService.extractBackendErrorMessage(error, 'Falha ao avisar pendência.')
                    });
                }
            });
    }

    cancelarSolicitacaoPessoaFisica(request: ConfirmarAverbacaoNomeRequest, motivo?: string): void {
        // A tela de detalhe já sabe se é PF ou PJ; barrar aqui o código fora do lado certo evita que
        // o `finalizarSolicitacao` (que aceita os dois) esconda um serviço apontado para o lado errado.
        if (!this.obterServicoCancelamentoPessoaFisica(request.codigoServico)) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: `Serviço sem endpoint de cancelamento mapeado: ${request.codigoServico}`
            });
            return;
        }

        this.executarFinalizacao(request, 'cancelar', motivo, 'Solicitação cancelada com sucesso.', 'Falha ao cancelar solicitação.');
    }

    cancelarSolicitacaoPessoaJuridica(request: ConfirmarAverbacaoNomeRequest, motivo?: string): void {
        if (!this.obterServicoCancelamentoPessoaJuridica(request.codigoServico)) {
            this.messageService.add({
                severity: 'error',
                summary: 'Erro',
                detail: `Serviço sem endpoint de cancelamento mapeado: ${request.codigoServico}`
            });
            return;
        }

        this.executarFinalizacao(request, 'cancelar', motivo, 'Solicitação cancelada com sucesso.', 'Falha ao cancelar solicitação.');
    }

    limparHistoricoTramitacao(): void {
        this.historicoTramitacaoSub?.unsubscribe();
        this.inserirHistoricoSub?.unsubscribe();
        this.confirmarSolicitacaoSub?.unsubscribe();
        this.historicoTramitacoes.set([]);
        this.carregandoHistorico.set(false);
        this.erroHistorico.set(null);
        this.inserindoHistorico.set(false);
        this.erroInsercaoHistorico.set(null);
        this.confirmandoSolicitacao.set(false);
        this.erroConfirmacaoSolicitacao.set(null);
        this.sucessoConfirmacaoSolicitacao.set(null);
        this.crmGeradoConfirmacao.set(null);
    }

    limparFeedbackConfirmacaoSolicitacao(): void {
        this.erroConfirmacaoSolicitacao.set(null);
        this.sucessoConfirmacaoSolicitacao.set(null);
        this.crmGeradoConfirmacao.set(null);
    }

    definirErroHistorico(mensagem: string): void {
        this.historicoTramitacoes.set([]);
        this.erroHistorico.set(mensagem);
        this.carregandoHistorico.set(false);
    }

    definirErroInsercaoHistorico(mensagem: string): void {
        this.erroInsercaoHistorico.set(mensagem);
        this.inserindoHistorico.set(false);
    }

    destruir(): void {
        this.servicosGeraisSub?.unsubscribe();
        this.protocolosSub?.unsubscribe();
        this.consultaProtocoloSub?.unsubscribe();
        this.historicoTramitacaoSub?.unsubscribe();
        this.inserirHistoricoSub?.unsubscribe();
        this.confirmarSolicitacaoSub?.unsubscribe();
    }

    private normalizarResponseConsulta(response: unknown): Record<string, unknown> {
        if (response && typeof response === 'object' && !Array.isArray(response)) {
            return response as Record<string, unknown>;
        }

        return {};
    }

    private normalizarUfsConsulta(ufsCrm: string[]): string[] {
        return Array.from(new Set((ufsCrm ?? []).map((uf) => (uf ?? '').trim().toUpperCase()).filter((uf) => !!uf)));
    }

    private deduplicarItensProtocolos(itens: ItemStatus[]): ItemStatus[] {
        const chaves = new Set<string>();
        const itensUnicos: ItemStatus[] = [];

        itens.forEach((item) => {
            const chave = this.montarChaveProtocolo(item);
            if (chaves.has(chave)) {
                return;
            }

            chaves.add(chave);
            itensUnicos.push(item);
        });

        return itensUnicos;
    }

    private enriquecerModalidadePrePrestador(codigoServico: string, itens: ItemStatus[]): Observable<ItemStatus[]> {
        if (codigoServico !== 'pre-prestador' || !itens.length) {
            return of(itens);
        }

        const consultas = itens.map((item, index) => {
            const numeroProtocolo = item.numeroProtocolo?.trim();
            const anoProtocolo = item.anoProtocolo?.trim();
            const ufCrm = item.ufCrm?.trim().toUpperCase();

            if (item.modalidadeInscricao || !numeroProtocolo || !anoProtocolo || !ufCrm) {
                return of(item);
            }

            const request: ConsultaProtocoloRequest = { numeroProtocolo, anoProtocolo, ufCrm };
            return consultarProtocoloPorCodigoServico(codigoServico, request, this.servicosAbertosPorChave).pipe(
                map((response) => {
                    const detalhe = mapearItemStatusProtocolo(this.normalizarResponseConsulta(response), index, ufCrm, request);
                    return {
                        ...item,
                        modalidadeInscricao: detalhe.modalidadeInscricao ?? item.modalidadeInscricao
                    };
                }),
                catchError(() => of(item))
            );
        });

        return forkJoin(consultas);
    }

    private finalizarAlteracaoSolicitacao(request: ConfirmarAverbacaoNomeRequest, mensagem: string, crmGerado: number | null = null): void {
        this.removerProtocoloDaLista(request);
        this.limparHistoricoTramitacao();
        this.sucessoConfirmacaoSolicitacao.set(mensagem);
        // Depois da limpeza de propósito: limparHistoricoTramitacao zera este sinal.
        this.crmGeradoConfirmacao.set(crmGerado);
        this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: mensagem
        });
        this.carregarProtocolosPorServico(request.codigoServico, [request.ufCrm]);
        this.alteracaoSolicitacaoConcluidaSubject.next({ manterDetalheAberto: crmGerado !== null });
    }

    private removerProtocoloDaLista(request: ConfirmarAverbacaoNomeRequest): void {
        const numero = request.numeroProtocolo.trim();
        const ano = request.anoProtocolo.trim();
        const uf = request.ufCrm.trim().toUpperCase();

        this.itensProtocolos.set(
            this.itensProtocolos().filter((item) => {
                const mesmoNumeroAno = (item.numeroProtocolo ?? '').trim() === numero && (item.anoProtocolo ?? '').trim() === ano;
                const itemUf = (item.ufCrm ?? '').trim().toUpperCase();
                const mesmaUf = !uf || !itemUf || itemUf === uf;
                return !(mesmoNumeroAno && mesmaUf);
            })
        );
    }

    /**
     * Troca `situacaoCodigo` só do item alvo, na lista que o painel de detalhe lê — a mesma que este
     * quadro usa para colorir a tag do card. Dono único do dado: se o quadro guardasse um mapa
     * próprio de situações, o card diria "Tramitado" enquanto o painel, lendo `itensProtocolos`,
     * continuaria dizendo "Ativo" para o mesmo protocolo na mesma tela.
     *
     * `MoverSolicitacaoResponse` não devolve a situação do portal: quem chama este método infere o
     * `'T'` de "a coluna de origem inicia atendimento e o mover foi aceito" — a mesma condição que
     * `KanbanServicosService.mover` usa no backend para decidir se tramita A → T. É correto hoje e
     * passa a mentir se essa regra mudar no backend sem ninguém atualizar aqui.
     */
    atualizarSituacaoProtocolo(numeroProtocolo: string, anoProtocolo: string, ufCrm: string, situacao: string): void {
        const numero = numeroProtocolo.trim();
        const ano = anoProtocolo.trim();
        const uf = ufCrm.trim().toUpperCase();

        this.itensProtocolos.set(
            this.itensProtocolos().map((item) => {
                const mesmoNumeroAno = (item.numeroProtocolo ?? '').trim() === numero && (item.anoProtocolo ?? '').trim() === ano;
                const itemUf = (item.ufCrm ?? '').trim().toUpperCase();
                const mesmaUf = !uf || !itemUf || itemUf === uf;
                // Mesma identidade dos demais objetos: o quadro usa `track item.portal`, e recriar
                // todos faria o Angular redesenhar cards que não mudaram.
                return mesmoNumeroAno && mesmaUf ? { ...item, situacaoCodigo: situacao } : item;
            })
        );
    }

    private montarChaveProtocolo(item: ItemStatus): string {
        const numero = (item.numeroProtocolo ?? '').trim();
        const ano = (item.anoProtocolo ?? '').trim();
        const uf = (item.ufCrm ?? '').trim().toUpperCase();

        if (numero && ano) {
            return `${numero}/${ano}-${uf}`;
        }

        return `${item.portal.trim()}|${item.sas.trim()}|${uf}`;
    }

    /**
     * O que o `consultar` não devolve e o portal exige na finalização:
     *
     * - `codigoServico`: consultório, visto e alteração PJ compartilham endpoint entre variantes
     *   (cadastro/renovação/cancelamento, corrido/fracionado, cadastral/contratual) e é só por ele
     *   que o backend distingue qual delas está sendo finalizada;
     * - a data ou a observação que o analista digitou no diálogo, que chega no `request`. Sem
     *   reinjetá-la aqui ela seria descartada em silêncio — falha pior que erro, porque ninguém vê.
     *
     * Quem diz qual campo cada serviço quer é o mesmo util que monta o diálogo, e não uma segunda
     * lista aqui: duplicada, é onde o serviço novo ganha campo na tela e o perde no envio.
     */
    private completarDtoQuandoOServicoExige(dto: unknown, request: ConfirmarAverbacaoNomeRequest, acao: AcaoServico): unknown {
        if (!dto || typeof dto !== 'object' || Array.isArray(dto)) {
            return dto;
        }

        const complemento: Record<string, unknown> = {};

        const isConsultorio =
            request.codigoServico === 'consultorio-privado' ||
            request.codigoServico === 'cadastro-consultorio-privado' ||
            request.codigoServico === 'consultorio-privado-renovacao' ||
            request.codigoServico === 'renovacao-consultorio-privado' ||
            request.codigoServico === 'consultorio-privado-cancelamento' ||
            request.codigoServico === 'cancelamento-consultorio-privado';
        const isVisto = request.codigoServico === 'visto-corrido' || request.codigoServico === 'visto-fracionado';
        const isAlteracaoPessoaJuridica = request.codigoServico === 'alteracao-cadastral' || request.codigoServico === 'alteracao-contratual' || request.codigoServico === 'visto-alteracao-contratual';

        // Alteração PJ só recebe o código ao efetivar: o cancelamento dela nunca mandou esse campo em
        // produção, e uniformizar os dois lados mudaria o corpo de uma chamada sem teste que a cubra.
        if (isConsultorio || isVisto || (isAlteracaoPessoaJuridica && acao === 'efetivar')) {
            complemento['codigoServico'] = request.codigoServico;
        }

        // Data e observação são campos DA CONFIRMAÇÃO: pertencem ao efetivar por definição. O cancelar
        // recebe o motivo por parâmetro próprio e nunca mandou esses campos no corpo — deixá-los
        // passar aqui mudaria, sem que ninguém pedisse, o payload do cancelamento em produção.
        // Só o que veio preenchido entra: campo vazio no request não pode apagar o que a consulta trouxe.
        if (acao === 'efetivar') {
            const chaveData = chaveDaDataDeConfirmacao(request.codigoServico);
            if (chaveData && request[chaveData]) {
                complemento[chaveData] = request[chaveData];
            }

            if (campoDaConfirmacao(request.codigoServico) === 'observacao' && request.observacao) {
                complemento['observacao'] = request.observacao;
            }
        }

        return Object.keys(complemento).length ? { ...(dto as Record<string, unknown>), ...complemento } : dto;
    }

    /**
     * Espelho do `obterServicoCancelamentoPessoaFisica`/`...PessoaJuridica` para o outro lado da ação.
     * Serviços de 2ª via, área de atuação e especialidade ficam de fora de propósito: só cancelam.
     */
    private obterServicoEfetivacao(codigoServico: string): EfetivavelSolicitacaoService | null {
        switch (codigoServico) {
            case 'averbacao-nome':
                return this.averbacaoNomeService;
            case 'averbacao-nacionalidade':
                return this.averbacaoNacionalidadeService;
            case 'cancelamento-falecimento':
            case 'cancelar-por-falecimento':
                return this.cancelamentoFalecimentoService;
            case 'cancelamento-inscricao':
            case 'cancelamento-de-inscricao':
                return this.cancelamentoInscricaoService;
            case 'consultorio-privado':
            case 'cadastro-consultorio-privado':
            case 'consultorio-privado-renovacao':
            case 'renovacao-consultorio-privado':
            case 'consultorio-privado-cancelamento':
            case 'cancelamento-consultorio-privado':
                return this.consultorioPrivadoService;
            case 'diretor-clinico':
            case 'diretoria-clinica':
                return this.diretorClinicoService;
            case 'pcmso':
            case 'responsavel-pcmso':
                return this.medicoPCSMOService;
            case 'pos':
            case 'pos-graduacao':
                return this.posGraduacaoService;
            case 'pre-medico':
                return this.preMedicoService;
            case 'visto-corrido':
            case 'visto-fracionado':
                return this.vistoCorridoService;
            case 'alteracao-cadastral':
            case 'alteracao-contratual':
            case 'visto-alteracao-contratual':
                return this.alteracaoCadastralPessoaJuridicaService;
            case 'cancelamento-pj':
                return this.cancelamentoPjService;
            case 'cart-evento':
                return this.cartEventoPessoaJuridicaService;
            case 'comissao-etica':
                return this.comissaoEticaService;
            case 'pre-prestador':
                return this.prePrestadorService;
            case 'regimento-interno':
            case 'regimento-interno-corpo-clinico':
                return this.regimentoInternoService;
            case 'renovacao':
            case 'renovacao-servicos':
            case 'renovacao-cadastral':
                return this.renovacaoCadastralService;
            case 'solicitacao-diretor':
            case 'diretor-tecnico-solicitacao-pj':
            case 'saida-diretor-tecnico':
                return this.solicitacaoDiretorService;
            case 'solicitacao-diretor-tecnico':
            case 'diretor-tecnico-solicitacao-pf':
                return this.solicitacaoDiretorTecnicoService;
            default:
                return null;
        }
    }

    private obterServicoCancelamentoPessoaFisica(codigoServico: string): CancelavelSolicitacaoService | null {
        switch (codigoServico) {
            case 'area-atuacao':
            case 'area-de-atuacao':
                return this.areaAtuacaoService;
            case 'averbacao-nacionalidade':
                return this.averbacaoNacionalidadeService;
            case 'averbacao-nome':
                return this.averbacaoNomeService;
            case 'cancelamento-falecimento':
            case 'cancelar-por-falecimento':
                return this.cancelamentoFalecimentoService;
            case 'cancelamento-inscricao':
            case 'cancelamento-de-inscricao':
                return this.cancelamentoInscricaoService;
            case 'solicitar-2via-carteira':
            case 'carteira-profissional':
                return this.carteiraProfissionalService;
            case 'solicitar-2via-cedula-identidade':
            case 'cedula-de-identidade':
                return this.cedulaIdentidadeService;
            case 'consultorio-privado':
            case 'cadastro-consultorio-privado':
            case 'consultorio-privado-renovacao':
            case 'renovacao-consultorio-privado':
            case 'consultorio-privado-cancelamento':
            case 'cancelamento-consultorio-privado':
                return this.consultorioPrivadoService;
            case 'diretor-clinico':
            case 'diretoria-clinica':
                return this.diretorClinicoService;
            case 'especialidade':
            case 'especialidades':
                return this.especialidadeMedicaService;
            case 'pcmso':
            case 'responsavel-pcmso':
                return this.medicoPCSMOService;
            case 'pos':
            case 'pos-graduacao':
                return this.posGraduacaoService;
            case 'pre-medico':
                return this.preMedicoService;
            case 'visto-corrido':
            case 'visto-fracionado':
                return this.vistoCorridoService;
            default:
                return null;
        }
    }

    private obterServicoCancelamentoPessoaJuridica(codigoServico: string): CancelavelSolicitacaoService | null {
        switch (codigoServico) {
            case 'cancelamento-pj':
                return this.cancelamentoPjService;
            case 'alteracao-cadastral':
            case 'alteracao-contratual':
            case 'visto-alteracao-contratual':
                return this.alteracaoCadastralPessoaJuridicaService;
            case 'comissao-etica':
                return this.comissaoEticaService;
            case 'pre-prestador':
                return this.prePrestadorService;
            case 'regimento-interno':
            case 'regimento-interno-corpo-clinico':
                return this.regimentoInternoService;
            case 'renovacao':
            case 'renovacao-servicos':
            case 'renovacao-cadastral':
                return this.renovacaoCadastralService;
            case 'solicitacao-diretor-tecnico':
            case 'diretor-tecnico-solicitacao-pf':
                return this.solicitacaoDiretorTecnicoService;
            case 'solicitacao-diretor':
            case 'diretor-tecnico-solicitacao-pj':
            case 'saida-diretor-tecnico':
                return this.solicitacaoDiretorService;
            case 'cart-evento':
                return this.cartEventoPessoaJuridicaService;
            default:
                return null;
        }
    }

    private extrairDataCancelamento(dataHora?: string): string | undefined {
        const texto = (dataHora ?? '').trim();
        if (!texto) {
            return undefined;
        }

        if (/^\d{2}\/\d{2}\/\d{4}/.test(texto)) {
            return texto.slice(0, 10);
        }

        const dataIso = texto.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(dataIso)) {
            const [ano, mes, dia] = dataIso.split('-');
            return `${dia}/${mes}/${ano}`;
        }

        return undefined;
    }

    private dataAtualFormatada(): string {
        const hoje = new Date();
        const dia = String(hoje.getDate()).padStart(2, '0');
        const mes = String(hoje.getMonth() + 1).padStart(2, '0');
        const ano = hoje.getFullYear();
        return `${dia}/${mes}/${ano}`;
    }
}
